import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { func } from 'prop-types'
import { Button, Dropdown, Menu, Modal, Progress, Upload } from 'antd'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faFileImport } from '@fortawesome/pro-solid-svg-icons'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import { onError, onSuccess, onWarning } from '../../../../utils/apiHelper'
import { SPREADSHEET_UPLOAD_TYPES } from '../../../../utils/constants'
import { getSectorsImportTemplate, importSectors, SectorImportModes } from '../../../../utils/api/sector'
import useFakeProgress from '../../../../hooks/useFakeProgress'
import useScreenSize from '../../../../hooks/useScreen'
import TooltipIconButton from '../../../../Components/antd/Buttons/TooltipIconButton'
import SectorsImportReport, { translateCode } from './SectorsImportReport'

import '../../../../assets/students-import-modal.scss'

const { Dragger } = Upload

const UPLOAD_WIDTH = 860
const REPORT_WIDTH = 1100
const DETAILED_REPORT_WIDTH = 1400
const REPORT_TOP = 24
const PROGRESS_INTERVAL = 400

const Steps = Object.freeze({
  UPLOAD: 'UPLOAD',
  PREVIEW: 'PREVIEW',
  DONE: 'DONE'
})

const ImportSectorsButton = ({ onImported = () => {} }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const screen = useScreenSize()
  const { percentage, start: startProgress, complete: completeProgress, reset: resetProgress } = useFakeProgress(PROGRESS_INTERVAL)

  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(Steps.UPLOAD)
  const [fileList, setFileList] = useState([])
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(null)

  const unmounted = useRef(false)

  useEffect(() => () => { unmounted.current = true }, [])

  const acceptedTypes = useMemo(() => SPREADSHEET_UPLOAD_TYPES.join(','), [])

  const reset = useCallback(() => {
    resetProgress()
    setStep(Steps.UPLOAD)
    setFileList([])
    setBusy(false)
    setReport(null)
  }, [resetProgress])

  const close = useCallback(() => {
    if (busy) {
      return
    }

    reset()
    setVisible(false)
  }, [busy, reset])

  const announceImport = useCallback(summary => {
    const written = summary.created + summary.updated

    if (written > 0) {
      onImported()
    }

    if (summary.errors === 0) {
      return onSuccess(t('sectors_import.import.success'))
    }

    return written > 0
      ? onWarning(t('sectors_import.import.partial'))
      : onError(t('sectors_import.import.failure'))
  }, [onImported, t])

  const send = useCallback((mode) => {
    if (fileList.length === 0) {
      return
    }

    setBusy(true)
    startProgress()

    importSectors(user, fileList[0], mode).then(received => {
      if (unmounted.current) {
        return
      }

      completeProgress()
      setReport(received)
      setStep(mode === SectorImportModes.IMPORT ? Steps.DONE : Steps.PREVIEW)
      setBusy(false)

      if (mode === SectorImportModes.IMPORT) {
        announceImport(received.summary)
      }
    }, error => {
      if (unmounted.current) {
        return
      }

      resetProgress()
      setBusy(false)

      onError(error && error.message
        ? translateCode(error.message, t)
        : t('sectors_import.errors.UNEXPECTED')
      )
    })
  }, [announceImport, completeProgress, fileList, resetProgress, startProgress, t, user])

  const downloadTemplate = useCallback(() => {
    getSectorsImportTemplate(user).then(() => {
      onSuccess(t('sectors_import.download_template.success'))
    }, error => {
      const code = error && !(error instanceof Error) ? error.message : null

      onError(code ? translateCode(code, t) : t('sectors_import.download_template.error'))
    })
  }, [t, user])

  const showingReport = step !== Steps.UPLOAD && report !== null

  const modalWidth = useMemo(() => {
    if (!showingReport) {
      return UPLOAD_WIDTH
    }

    const detailed = report.data.some(item => item.errors || item.warnings)

    return Math.min(detailed ? DETAILED_REPORT_WIDTH : REPORT_WIDTH, Math.round(screen.width * 0.9))
  }, [report, screen.width, showingReport])

  const writableRows = useMemo(
    () => report === null ? 0 : report.summary.total - report.summary.errors,
    [report]
  )

  const menu = (
    <Menu>
      <Menu.Item onClick={() => { reset(); setVisible(true) }}>
        <FontAwesomeIcon icon={faFileImport} />&nbsp;
        {t('sectors_import.tooltip_title')}
      </Menu.Item>
      <Menu.Item onClick={downloadTemplate}>
        <FontAwesomeIcon icon={faDownload} />&nbsp;
        {t('sectors_import.download_template')}
      </Menu.Item>
    </Menu>
  )

  const footer = useMemo(() => {
    if (step === Steps.DONE) {
      return [
        <Button key='close' type='primary' onClick={close}>{t('Close')}</Button>
      ]
    }

    if (step === Steps.PREVIEW) {
      return [
        <Button key='back' disabled={busy} onClick={() => setStep(Steps.UPLOAD)}>{t('Back')}</Button>,
        <Button
          key='confirm'
          type='primary'
          loading={busy}
          disabled={busy || writableRows === 0}
          onClick={() => send(SectorImportModes.IMPORT)}
        >
          {t('sectors_import.confirm')}
        </Button>
      ]
    }

    return [
      <Button key='cancel' disabled={busy} onClick={close}>{t('Cancel')}</Button>,
      <Button
        key='analyse'
        type='primary'
        loading={busy}
        disabled={busy || fileList.length === 0}
        onClick={() => send(SectorImportModes.DRY_RUN)}
      >
        {t('sectors_import.analyse')}
      </Button>
    ]
  }, [busy, close, fileList.length, send, step, t, writableRows])

  return (
    <>
      <Dropdown overlay={menu} trigger={['click']}>
        <TooltipIconButton title={t('sectors_import.tooltip_title')} icon={faFileImport} />
      </Dropdown>
      <Modal
        title={t('sectors_import.modal_title')}
        visible={visible}
        width={modalWidth}
        style={showingReport ? { top: REPORT_TOP } : undefined}
        footer={footer}
        maskClosable={!busy}
        closable={!busy}
        onCancel={close}
        destroyOnClose
      >
        <div className='students-import-modal-body sectors-import-modal-body'>
          {busy && (
            <div className='importing-animation'>
              <Progress type='circle' percent={percentage} />
              <p className='importing-label'>
                {step === Steps.PREVIEW ? t('sectors_import.importing') : t('sectors_import.analysing')}
              </p>
            </div>
          )}
          {!busy && step === Steps.UPLOAD && (
            <div className='upload-area'>
              <Dragger
                fileList={fileList}
                name='file'
                accept={acceptedTypes}
                beforeUpload={(file) => {
                  if (!SPREADSHEET_UPLOAD_TYPES.includes(file.type)) {
                    onError(t('sectors_import.errors.UNSUPPORTED_FILE_TYPE'))

                    return false
                  }

                  setFileList([file])

                  return false
                }}
                onRemove={() => setFileList([])}
              >
                <p className='ant-upload-text'>{t('sectors_import.upload_text')}</p>
                <p className='ant-upload-hint'>{t('sectors_import.upload_hint')}</p>
              </Dragger>
            </div>
          )}
          {!busy && step !== Steps.UPLOAD && report !== null && (
            <SectorsImportReport report={report} height={screen.height} t={t} />
          )}
        </div>
      </Modal>
    </>
  )
}

ImportSectorsButton.propTypes = {
  onImported: func
}

export default ImportSectorsButton
